import React, { useContext, useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid-pro';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { translate as t } from 'hooks/useTranslations';
import datetimeHelper from 'helpers/datetimeHelper';
import PhxBadge from '_lex/_atoms/PhxBadge';
import useReviewWorkflows from '../_data_sources/useReviewWorkflows';
import { ReviewWorkflowListItem, ReviewWorkflowStatus } from '../_types';
import { AppContext } from 'views/App/context';

const EMPTY_VALUE_PLACEHOLDER = '--';

const getStatusBadgeColor = (status?: ReviewWorkflowStatus): 'success' | 'error' | 'default' => {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower === 'active') return 'success';
  if (statusLower === 'inactive') return 'error';
  return 'default';
};

const ReviewWorkflowsList: React.FC = () => {
  const { currentOrganizationId } = useContext(AppContext);
  const queryParams = useMemo(() => {
    return {
      organizationId: currentOrganizationId,
    };
  }, [currentOrganizationId]);
  const reviewWorkflowsQuery = useReviewWorkflows(queryParams);
  const results = reviewWorkflowsQuery.data?.pages.flatMap((page) => page.content) ?? [];

  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('performanceReporting.reviewWorkflows.list.columns.name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row as ReviewWorkflowListItem;
          if (!row) return null;
          return (
            <Typography
              variant='body2'
              fontWeight='medium'
            >
              {row.name}
            </Typography>
          );
        },
      },
      templates: {
        header: t('performanceReporting.reviewWorkflows.list.columns.template'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row as ReviewWorkflowListItem;
          if (!row || !row.templates || row.templates.length === 0) {
            return (
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {EMPTY_VALUE_PLACEHOLDER}
              </Typography>
            );
          }

          const firstTemplate = row.templates[0];
          const remainingCount = row.templates.length - 1;

          if (remainingCount === 0) {
            return <Typography variant='body2'>{firstTemplate}</Typography>;
          }

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2'>{firstTemplate}</Typography>
              <Tooltip
                title={
                  <Box>
                    {row.templates.slice(1).map((template) => (
                      <Typography
                        key={template}
                        variant='body2'
                      >
                        {template}
                      </Typography>
                    ))}
                  </Box>
                }
                arrow
                placement='top'
              >
                <Typography
                  variant='body2'
                  sx={{
                    color: 'primary.main',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('performanceReporting.reviewWorkflows.list.moreTemplates', '', { count: String(remainingCount) })}
                </Typography>
              </Tooltip>
            </Box>
          );
        },
      },
      createdBy: {
        header: t('performanceReporting.reviewWorkflows.list.columns.createdBy'),
        flex: 1.5,
        renderer: (params: GridCellParams) => {
          const row = params.row as ReviewWorkflowListItem;
          if (!row) return null;
          return <Typography variant='body2'>{row.createdBy || EMPTY_VALUE_PLACEHOLDER}</Typography>;
        },
      },
      createdAt: {
        header: t('performanceReporting.reviewWorkflows.list.columns.createdAt'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row as ReviewWorkflowListItem;
          if (!row) return null;
          return (
            <Typography variant='body2'>
              {datetimeHelper.formatDate(row.createdAt, { format: 'short', missingDisplay: EMPTY_VALUE_PLACEHOLDER })}
            </Typography>
          );
        },
      },
      status: {
        header: t('performanceReporting.reviewWorkflows.list.columns.status'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row as ReviewWorkflowListItem;
          if (!row) return null;

          return (
            <PhxBadge
              color={getStatusBadgeColor(row.status)}
              size='sm'
            >
              {row.status}
            </PhxBadge>
          );
        },
      },
    }),
    [t]
  );

  return (
    <CommonLayout
      pageTitle={t('performanceReporting.navItems.reviewWorkflows')}
      headerProps={{
        title: t('performanceReporting.reviewWorkflows.list.title'),
        subtitle: t('performanceReporting.reviewWorkflows.list.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: '1fr', gap: 4, minHeight: 0 }}>
        <GridSection
          columns={columns}
          data={results}
          query={reviewWorkflowsQuery}
          gridProps={{
            getRowId: (row: ReviewWorkflowListItem) => row.reviewWorkflowId,
          }}
        />
      </Box>
    </CommonLayout>
  );
};

export default ReviewWorkflowsList;
